import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faTimes, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

/**
 * FilterPanel component for filtering and grouping orders/quotations
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.updateFilter - Function to update a single filter
 * @param {Function} props.resetFilters - Function to reset all filters
 * @param {Function} props.onApply - Callback when filters are applied (for mobile)
 * @param {Object} props.textData - Translation data
 * @param {Array} props.statusOptions - Available status options
 * @param {Boolean} props.responsive - Whether in mobile view
 */
export default function FilterPanel({
    filters,
    updateFilter,
    resetFilters,
    onApply,
    textData,
    statusOptions = [],
    responsive = false,
}) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);

    // Count active filters
    const activeFilterCount = [
        filters.status?.length > 0,
        filters.date_from,
        filters.date_to,
        filters.price_min,
        filters.price_max,
        filters.group_by,
        filters.search,
    ].filter(Boolean).length;

    // Close mobile panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (mobileOpen && !e.target.closest(".filter-panel-mobile") && !e.target.closest(".filter-toggle-btn")) {
                setMobileOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [mobileOpen]);

    // Handle status checkbox change
    const handleStatusChange = (statusValue) => {
        const currentStatus = filters.status || [];
        let newStatus;

        if (currentStatus.includes(statusValue)) {
            newStatus = currentStatus.filter((s) => s !== statusValue);
        } else {
            newStatus = [...currentStatus, statusValue];
        }

        updateFilter("status", newStatus.length > 0 ? newStatus : null);
    };

    // Handle sort change
    const handleSortChange = (e) => {
        const value = e.target.value;
        if (value === "") {
            updateFilter("sort_by", null);
            updateFilter("sort_direction", null);
        } else {
            // Split on last underscore to handle field names with underscores (e.g., created_at)
            const lastUnderscoreIndex = value.lastIndexOf("_");
            const sortBy = value.substring(0, lastUnderscoreIndex);
            const sortDirection = value.substring(lastUnderscoreIndex + 1);
            updateFilter("sort_by", sortBy);
            updateFilter("sort_direction", sortDirection);
        }
    };

    // Get current sort value for select
    const getCurrentSortValue = () => {
        if (filters.sort_by && filters.sort_direction) {
            return `${filters.sort_by}_${filters.sort_direction}`;
        }
        return "";
    };

    // Handle apply on mobile
    const handleApply = () => {
        setMobileOpen(false);
        if (onApply) {
            onApply();
        }
    };

    // Handle reset
    const handleReset = () => {
        resetFilters();
        if (responsive) {
            setMobileOpen(false);
        }
        if (onApply) {
            onApply();
        }
    };

    const filterContent = (
        <div className="filter-panel-content">
            {/* Status Filter */}
            <div className="filter-group">
                <label className="filter-label">{textData?.["filters"]?.["status"] || "Status"}</label>
                <div className="status-dropdown">
                    <button
                        type="button"
                        className="status-dropdown-toggle"
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    >
                        <span>
                            {filters.status && filters.status.length > 0
                                ? `${filters.status.length} ${textData?.["filters"]?.["selected"] || "selected"}`
                                : textData?.["filters"]?.["all_statuses"] || "All statuses"}
                        </span>
                        <FontAwesomeIcon icon={faChevronDown} className={statusDropdownOpen ? "rotated" : ""} />
                    </button>
                    {statusDropdownOpen && (
                        <div className="status-dropdown-menu">
                            {statusOptions.map((option) => (
                                <label key={option.value} className="status-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={(filters.status || []).includes(option.value)}
                                        onChange={() => handleStatusChange(option.value)}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Date Range Filter */}
            <div className="filter-group">
                <label className="filter-label">{textData?.["filters"]?.["date_range"] || "Date Range"}</label>
                <div className="date-range-inputs">
                    <input
                        type="date"
                        value={filters.date_from || ""}
                        onChange={(e) => updateFilter("date_from", e.target.value || null)}
                        placeholder={textData?.["filters"]?.["from"] || "From"}
                    />
                    <span className="date-separator">-</span>
                    <input
                        type="date"
                        value={filters.date_to || ""}
                        onChange={(e) => updateFilter("date_to", e.target.value || null)}
                        placeholder={textData?.["filters"]?.["to"] || "To"}
                    />
                </div>
            </div>

            {/* Price Range Filter */}
            {/* <div className="filter-group">
                <label className="filter-label">{textData?.["filters"]?.["price_range"] || "Price Range"}</label>
                <div className="price-range-inputs">
                    <div className="price-input-wrapper">
                        <span className="currency-symbol">€</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={filters.price_min || ""}
                            onChange={(e) => updateFilter("price_min", e.target.value || null)}
                            placeholder={textData?.["filters"]?.["min"] || "Min"}
                        />
                    </div>
                    <span className="price-separator">-</span>
                    <div className="price-input-wrapper">
                        <span className="currency-symbol">€</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={filters.price_max || ""}
                            onChange={(e) => updateFilter("price_max", e.target.value || null)}
                            placeholder={textData?.["filters"]?.["max"] || "Max"}
                        />
                    </div>
                </div>a
            </div> */}

            {/* Sort */}
            <div className="filter-group">
                <label className="filter-label">{textData?.["filters"]?.["sort_by"] || "Sort By"}</label>
                <select value={getCurrentSortValue()} onChange={handleSortChange} className="filter-select">
                    <option value="">{textData?.["filters"]?.["default_sort"] || "Newest First"}</option>
                    <option value="created_at_desc">{textData?.["filters"]?.["date_newest"] || "Date (Newest)"}</option>
                    <option value="created_at_asc">{textData?.["filters"]?.["date_oldest"] || "Date (Oldest)"}</option>
                    <option value="total_price_desc">
                        {textData?.["filters"]?.["price_high"] || "Price (High to Low)"}
                    </option>
                    <option value="total_price_asc">
                        {textData?.["filters"]?.["price_low"] || "Price (Low to High)"}
                    </option>
                    <option value="reference_asc">
                        {textData?.["filters"]?.["reference_az"] || "Reference (A-Z)"}
                    </option>
                    <option value="reference_desc">
                        {textData?.["filters"]?.["reference_za"] || "Reference (Z-A)"}
                    </option>
                </select>
            </div>

            {/* Group By */}
            <div className="filter-group">
                <label className="filter-label">{textData?.["filters"]?.["group_by"] || "Group By"}</label>
                <select
                    value={filters.group_by || ""}
                    onChange={(e) => updateFilter("group_by", e.target.value || null)}
                    className="filter-select"
                >
                    <option value="">{textData?.["filters"]?.["no_grouping"] || "No Grouping"}</option>
                    <option value="status">{textData?.["filters"]?.["group_status"] || "Status"}</option>
                    <option value="month">{textData?.["filters"]?.["group_month"] || "Month"}</option>
                </select>
            </div>

            {/* Search */}
            <div className="filter-group filter-group-search">
                <label className="filter-label">{textData?.["filters"]?.["search"] || "Search"}</label>
                <input
                    type="text"
                    value={filters.search || ""}
                    onChange={(e) => updateFilter("search", e.target.value || null)}
                    placeholder={textData?.["filters"]?.["search_placeholder"] || "Order # or Reference"}
                    className="filter-input"
                />
            </div>

            <div className="filter-group">
                <button type="button" className="filter-reset-btn-desktop" onClick={handleReset}>
                    {textData?.["filters"]?.["reset"] || "Reset"}
                </button>
            </div>

            {/* Action buttons for mobile */}
            {responsive && (
                <div className="filter-actions">
                    <button type="button" className="filter-reset-btn" onClick={handleReset}>
                        {textData?.["filters"]?.["reset"] || "Reset"}
                    </button>
                    <button type="button" className="filter-apply-btn" onClick={handleApply}>
                        {textData?.["filters"]?.["apply"] || "Apply"}
                    </button>
                </div>
            )}
        </div>
    );

    // Desktop view
    if (!responsive) {
        return (
            <div className={`filter-panel filter-panel-desktop ${isCollapsed ? "collapsed" : ""}`}>
                <div className="filter-panel-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
                    <div className="filter-panel-toggle-left">
                        <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                        <span className="filter-toggle-text">{textData?.["filters"]?.["filters"] || "Filters"}</span>
                        {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
                    </div>
                    <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} className="filter-toggle-icon" />
                </div>
                <div className={`filter-panel-body ${isCollapsed ? "hidden" : ""}`}>{filterContent}</div>
            </div>
        );
    }

    // Mobile view
    return (
        <>
            <button type="button" className="filter-toggle-btn" onClick={() => setMobileOpen(true)}>
                <FontAwesomeIcon icon={faFilter} />
                <span>{textData?.["filters"]?.["filters"] || "Filters"}</span>
                {(filters.status?.length > 0 ||
                    filters.date_from ||
                    filters.date_to ||
                    filters.price_min ||
                    filters.price_max ||
                    filters.group_by) && <span className="filter-badge">●</span>}
            </button>

            {/* Mobile overlay */}
            {mobileOpen && <div className="filter-panel-overlay" onClick={() => setMobileOpen(false)} />}

            {/* Mobile slide-out panel */}
            <div className={`filter-panel-mobile ${mobileOpen ? "open" : ""}`}>
                <div className="filter-panel-header">
                    <h3>{textData?.["filters"]?.["filters"] || "Filters"}</h3>
                    <button type="button" className="filter-close-btn" onClick={() => setMobileOpen(false)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                {filterContent}
            </div>
        </>
    );
}
